<template>
  <div v-if="opqIf">
    <infobulle :descriptionPopup="descriptionPopup">
      <label :for="idInner" v-if="libelleLong">
        <div v-html="libelleLong" class="mb-1"></div>
      </label>
      <v-text-field
        :id="idInner"
        :style="{ width: width + 'px' }"
        v-bind="$attrs"
        :value="innerValue"
        outlined
        :hint="innerFacade"
        :persistent-hint="!!innerFacade"
        :rules="textFieldRules"
        @input="handleInput"
        @blur="handleBlur"
        :label="label"
        v-facade="innerFacade ? innerFacade : ''"
        :loading="updating"
        :disabled="updating || disabled"
        dense
        :type="isNumber || isDecimal ? 'number' : 'text'"
        min="0"
        ref="textField"
        :counter="showCounter"
        :maxlength="maxLength"
        :step="isNumber || isDecimal ? step : ''"
      ></v-text-field>
    </infobulle>
  </div>
</template>

<script>
import ChampBase from "@/mixins/ChampBase"
import { Queue, Api } from "@/plugins/api"

// @group Champs
export default {
  props: {
    width: { type: String, default: "270" },
    value: [String, Number],
    code: { type: String, required: true },
    optional: { type: Boolean, default: false },
    facade: { type: String, default: "" },
    isEmail: { type: Boolean, default: false },
    isNumber: { type: Boolean, default: false },
    isDecimal: { type: Boolean, default: false },
    live: { type: Object },
    isPhoneNumber: { type: Boolean, default: false },
    isCodePostal: { type: Boolean, default: false },
    isNumeroReconnaissance: { type: Boolean, default: false },
    codePays: { type: String, default: "CA" },
    // Variable qui lorsqu'elle passe à false, déclenche un clear du champ
    opqIf: { type: Boolean, default: true },
    id: { type: String },
    disabled: { type: Boolean, default: false },
    longueurMax: { type: Number, default: 100 },
    step: { type: String, default: "1" },
    customRegex: { type: String }
  },

  data() {
    return {
      updating: false,
      innerValue: "",
      phoneMasks: {
        CA: { facade: "(###)###-####", regex: /^\(\d{3}\)\d{3}-\d{4}$/ },
        US: { facade: "(###)###-####", regex: /^\(\d{3}\)\d{3}-\d{4}$/ }
      },
      codePostalMasks: {
        CA: { facade: "A#A #A#", regex: /^[A-Z]\d[A-Z] \d[A-Z]\d$/ },
        BJ: { facade: "####", regex: /^\d{4}$/ },
        BE: { facade: "####", regex: /^\d{4}$/ },
        DE: { facade: "####", regex: /^\d{4}$/ },
        NL: { facade: "####-AA", regex: /^\d{4}-[A-Z][A-Z]$/ },
        IL: { facade: "#####", regex: /^\d{5}$/ },
        IT: { facade: "#####", regex: /^\d{5}$/ },
        MG: { facade: "###", regex: /^\d{3}$/ },
        ES: { facade: "#####", regex: /^\d{5}$/ },
        MQ: { facade: "#####", regex: /^\d{5}$/ },
        GR: { facade: "#####", regex: /^\d{5}$/ },
        IN: { facade: "### ###", regex: /^\d{3} \d{3}$/ },
        CH: { facade: "####", regex: /^\d{4}$/ },
        FR: { facade: "#####", regex: /^\d{5}$/ },
        US: { facade: "#####", regex: /^\d{5}$/ },
        AU: { facade: "####", regex: /^\d{4}$/ },
        GF: { facade: "#####", regex: /^\d{5}$/ },
        FI: { facade: "#####", regex: /^\d{5}$/ }
      },
      numeroReconnaissanceMask: { regex: /^R[A,E,P][0-9]{5}-[0-9]{2}$/ }
    }
  },

  created() {
    this.innerValue = this.value
  },

  mixins: [ChampBase],

  methods: {
    handleInput(event) {
      this.innerValue = event

      this.$emit("input", event)
    },

    handleBlur(event, priority = 1) {
      if (this.live && (event.target.value ?? "").length <= this.maxLength) {
        this.updating = true
        Queue.enqueue(
          () =>
            Api.post(this.live.endpoint, {
              fileId: this.live.fileId,
              cle: this.live.cle,
              value: event.target.value
            }).finally(() => {
              this.$emit("eudo-updated", event)
              this.updating = false
            }),
          priority
        )
      }
      this.$emit("blur", event)
    },
    setValue(value) {
      this.innerValue = value
      this.handleBlur({ target: { value } }, 8)
      this.handleInput(value)
    }
  },

  computed: {
    textFieldRules() {
      if (this.isNumber) {
        return this.numberRules
      } else if (this.isDecimal) {
        return this.decimalRules
      } else if (this.isEmail) {
        return this.emailRules
      } else if (this.isCodePostal) {
        return this.codePostalRules
      } else if (this.isPhoneNumber) {
        return this.phoneNumberRules
      } else if (this.isNumeroReconnaissance) {
        return this.numeroReconnaissanceRules
      } else if (this.optional) {
        return this.optionalRules
      }
      return [(v) => !!v || "Champ requis"]
    },
    optionalRules() {
      return [(v) => !v || v.toString().length <= this.maxLength || "Valeur trop longue."]
    },
    numberRules() {
      return [(v) => (this.regex.test(v) && (v ?? "").toString().length <= this.maxLength) || "Veuillez entrer un nombre"]
    },
    decimalRules() {
      return [(v) => (this.regex.test(v) && (v ?? "").toString().length <= this.maxLength) || "Veuillez entrer un nombre"]
    },
    emailRules() {
      return [(v) => (this.regex.test(v) && (v ?? "").length <= this.maxLength) || "Courriel non valide"]
    },
    codePostalRules() {
      return [(v) => (this.regex.test(v) && (v ?? "").length <= this.maxLength) || this.innerFacade || "Champ requis"]
    },
    phoneNumberRules() {
      return [(v) => (this.regex.test(v) && (v ?? "").length <= this.maxLength) || this.innerFacade || "Champ requis"]
    },
    numeroReconnaissanceRules() {
      return [(v) => (this.regex.test(v) && (v ?? "").length <= this.maxLength) || this.innerFacade || "XX#####-## (ex. RA12345-22)"]
    },
    phoneMask() {
      return (
        this.phoneMasks[this.codePays] ?? {
          facade: "",
          regex: new RegExp(this.optional ? "" : /.+/)
        }
      )
    },
    codePostalMask() {
      return (
        this.codePostalMasks[this.codePays] ?? {
          facade: "",
          regex: new RegExp(this.optional ? "" : /.+/)
        }
      )
    },
    regex() {
      if (this.isNumber) {
        return this.optional ? (this.innerValue ? this.numberRegex : new RegExp("")) : this.numberRegex
      } else if (this.isDecimal) {
        return this.optional ? (this.innerValue ? this.decimalRegex : new RegExp("")) : this.decimalRegex
      } else if (this.isPhoneNumber) {
        return this.optional ? (this.innerValue ? this.phoneMask.regex : new RegExp("")) : this.phoneMask.regex
      } else if (this.isCodePostal) {
        return this.optional ? (this.innerValue ? this.codePostalMask.regex : new RegExp("")) : this.codePostalMask.regex
      } else if (this.isEmail) {
        return this.optional ? (this.innerValue ? this.$emailRegex : new RegExp("")) : this.$emailRegex
      } else if (this.isNumeroReconnaissance) {
        return this.optional ? (this.innerValue ? this.numeroReconnaissanceMask.regex : new RegExp("")) : this.numeroReconnaissanceMask.regex
      }

      return new RegExp(this.optional ? "" : /.+/)
    },
    numberRegex() {
      return /^\d+$/
    },
    decimalRegex() {
      if (this.customRegex) {
        return RegExp(this.customRegex)
      } else {
        return /^\d+([.,]d+)?$/
      }
    },
    innerFacade() {
      if (this.facade) {
        return this.facade
      }

      if (this.isPhoneNumber) {
        return this.phoneMask.facade
      } else if (this.isCodePostal) {
        return this.codePostalMask.facade
      }

      return ""
    },
    showCounter() {
      return !!this.innerValue && this.innerValue.toString().length >= 80
    },
    maxLength() {
      if (this.isNumber) {
        return this.longueurMax
      } else if (this.isEmail) {
        return this.longueurMax
      } else if (this.isCodePostal) {
        return this.codePostalMask.facade?.length || this.longueurMax
      } else if (this.isPhoneNumber) {
        return this.phoneMask.facade?.length || this.longueurMax
      } else if (this.isNumeroReconnaissance) {
        return 10
      }
      return this.longueurMax
    }
  },

  watch: {
    opqIf(newValue) {
      if (!newValue) {
        this.innerValue = ""
        this.$refs.textField.clearableCallback()
        this.handleBlur({ target: { value: null } })
      }
    },
    value(newVal) {
      this.innerValue = newVal
    }
  }
}
</script>

<style>
</style>